<template>
  <div class="sv-summary" data-test="sv-summary">
    <!-- Wraps on a wide screen; one swipeable row on a phone (see the phone @media block). -->
    <div class="sv-summary__cards">
      <button
        v-for="card in statusCards"
        :key="`s-${card.bucket}`"
        type="button"
        class="sv-summary__card"
        :class="{ 'sv-summary__card--on': isStatusOn(card.bucket) }"
        :data-test="`sv-summary-status-${card.bucket}`"
        @click="$emit('toggle-status', card.bucket)"
      >
        <span class="sv-summary__count">{{ card.count }}</span>
        <span class="sv-summary__label">
          <span class="sv-summary__dot" :class="`sv-summary__dot--${card.bucket}`"></span>
          {{ card.label }}
        </span>
      </button>

      <span class="sv-summary__divider" aria-hidden="true"></span>

      <button
        v-for="card in alertCards"
        :key="`a-${card.key}`"
        type="button"
        class="sv-summary__card"
        :class="{ 'sv-summary__card--on': isAlertOn(card.key) }"
        :title="card.label"
        :data-test="`sv-summary-alert-${card.key}`"
        @click="$emit('toggle-alert', card.key)"
      >
        <span class="sv-summary__count">{{ card.count }}</span>
        <span class="sv-summary__label">
          <i class="fa sv-summary__icon" :class="card.icon" aria-hidden="true"></i>
          {{ card.label }}
        </span>
        <span v-if="card.total" class="sv-summary__total">{{ card.total }}</span>
      </button>
    </div>

    <!--
      An alert that FAILED and an alert that found NOTHING both count zero. Saying so is the whole
      point: "0 patients owe you money" is a claim, and we must not make it when we do not know.
    -->
    <p v-if="failed.length" class="sv-summary__error" data-test="sv-summary-error">
      {{ failedSentence }}
    </p>
  </div>
</template>

<script>
/**
 * The summary strip above the grid: how the day looks at a glance, and a one-click way to narrow
 * to whatever needs attention.
 *
 * ── CARDS COME FROM WHAT THE SERVER RAN ──────────────────────────────────────────────────────
 * Not from the settings. The settings say what the office wants; `ran` says what was actually
 * computed. Wave 1 implements five of the eleven alerts, so a settings-driven strip would show
 * "Medical history due: 0" for every practice — a confident, permanent, wrong answer.
 *
 * ── THE COUNTS ARE PER APPOINTMENT, THE TOTALS ARE PER PATIENT ───────────────────────────────
 * "6 need a cleaning booked" counts today's appointments, which is what the office is looking at.
 * The money on the card is deduplicated per patient (and per guarantor for a family balance),
 * because one debt shown on two siblings' blocks is still one debt.
 */
import { ALERT_META } from '@/utils/scheduleView/settingsForm';
import { alertTotal, formatMoney } from '@/utils/scheduleView/alerts';

const META_BY_KEY = {};
ALERT_META.forEach((a) => { META_BY_KEY[a.key] = a; });

const STATUS_LABELS = [
  { bucket: 'unconfirmed', label: 'Not confirmed' },
  { bucket: 'confirmed', label: 'Confirmed' },
  { bucket: 'arrived', label: 'Arrived' },
  { bucket: 'completed', label: 'Completed' },
];

/** Alerts whose detail carries money worth totalling on the card. */
const MONEY_ALERTS = ['balance', 'claim', 'unsched'];

export default {
  name: 'ScheduleSummaryBar',

  props: {
    /** The output of utils/scheduleView/alerts.js summaryCounts(). */
    counts: { type: Object, required: true },
    /** The appointments the counts were computed from, for the money totals. */
    appointments: { type: Array, default: () => [] },
    /** The raw /alerts payload. */
    alertPayload: { type: Object, default: null },
    /** The active filters, so a card can show itself as the reason the grid is narrowed. */
    filters: { type: Object, default: () => ({}) },
  },

  computed: {
    statusCards() {
      const statuses = this.counts.statuses || {};
      return STATUS_LABELS.map(s => ({ ...s, count: statuses[s.bucket] || 0 }));
    },

    alertCards() {
      const ran = this.counts.ran || [];
      const alerts = this.counts.alerts || {};
      // ALERT_META order, not `ran` order, so the strip does not reshuffle when a rule fails.
      return ALERT_META
        .filter(meta => ran.indexOf(meta.key) !== -1)
        .map(meta => ({
          key: meta.key,
          icon: meta.icon,
          label: meta.label,
          count: alerts[meta.key] || 0,
          total: this.moneyFor(meta.key),
        }));
    },

    failed() {
      return this.counts.errors || [];
    },

    failedSentence() {
      const names = this.failed.map(k => (META_BY_KEY[k] ? META_BY_KEY[k].label : k));
      const list = names.join(', ');
      return names.length === 1
        ? `“${list}” could not be checked for this day — it is not counted above.`
        : `These could not be checked for this day and are not counted above: ${list}.`;
    },
  },

  methods: {
    moneyFor(key) {
      if (MONEY_ALERTS.indexOf(key) === -1) return '';
      const total = alertTotal(this.appointments, this.alertPayload, key);
      return total > 0 ? formatMoney(total) : '';
    },

    isStatusOn(bucket) {
      return ((this.filters && this.filters.statuses) || []).indexOf(bucket) !== -1;
    },

    isAlertOn(key) {
      return ((this.filters && this.filters.alerts) || []).indexOf(key) !== -1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-summary {
  margin-bottom: 12px;
}

.sv-summary__cards {
  display: flex;
  align-items: stretch;
  gap: 6px;
  flex-wrap: wrap;
}

.sv-summary__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  min-width: 92px;
  padding: 7px 10px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  text-align: left;
  cursor: pointer;

  &:hover { border-color: $sv-brand; }

  &--on {
    border-color: $sv-brand;
    box-shadow: inset 0 0 0 1px $sv-brand;
  }
}

.sv-summary__count {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  color: $sv-ink;
}

.sv-summary__label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: $sv-ink-3;
  white-space: nowrap;
}

.sv-summary__icon {
  width: 11px;
  text-align: center;
}

.sv-summary__total {
  font-size: 11px;
  font-weight: 700;
  color: $sv-ink-2;
}

.sv-summary__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: 0 0 auto;

  &--confirmed { background: $sv-status-confirmed; }
  &--unconfirmed { background: $sv-status-unconfirmed; }
  &--arrived { background: $sv-status-arrived; }
  &--completed { background: $sv-status-completed; }
}

.sv-summary__divider {
  width: 1px;
  margin: 4px 2px;
  background: $sv-line;
}

.sv-summary__error {
  margin: 6px 0 0;
  font-size: 11.5px;
  font-weight: 600;
  color: #a5292e;
}

/**
 * Phone: ONE row that swipes sideways, as in the design's mobile layout.
 *
 * Wrapped, four status cards plus the alert cards stack three or four rows deep and push the
 * first appointment below the fold — on the screen where the schedule matters most. The last card
 * is cut by the edge on purpose: that cut is what tells somebody the row scrolls.
 */
@media (max-width: $sv-phone-max) {
  .sv-summary__cards {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    // Room for the selected card's inset ring, which would otherwise be clipped by the scroller.
    padding: 1px 1px 2px;

    &::-webkit-scrollbar { display: none; }
  }

  .sv-summary__card {
    flex: 0 0 auto;
    min-width: 78px;
    padding: 6px 8px;
  }

  .sv-summary__count { font-size: 16px; }
}
</style>
